import React, { useState } from "react";
import { ButtonGroup, Form, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { useRequest } from "@components/AppHook";
import { DevForm } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { RenderForm } from "@components/Forms/Render";
import { Awesome, DevModal, OrderingHeader } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { CommonCard, ListingCard } from "@components/Pages";
import { getAttachement, url_reverse } from "@components/Utils";

export default function MarkerPromocodeScreen() {
    const { t } = useTranslation();
    const params = useParams();
    const header = t("Промокоды");

    let body = "";
    if (params.action) {
        if (params.action === "generation") {
            body = <BatchGeneration header={header} />;
        } else if (params.action === "log") {
            body = <Log header={header} />;
        } else if (params.action === "ban") {
            body = <Ban header={header} />;
        }
    } else {
        body = <List header={header} />;
    }
    return <DefaultLayout>{body} </DefaultLayout>;
}

const List = ({ header }) => {
    const { t } = useTranslation();
    const { apiData, apiUrl } = useAppApi(url_reverse("api:market:promocode:list"));
    const { api_get } = useRequest();
    const { setDelData } = useAppContext();

    if (!apiData) return;

    const getCSV = (ev, batch) => {
        if (batch.quantity > 50000) {
            api_get(ev, apiUrl + "?export_batch_letter=" + batch.id);
        } else {
            getAttachement(apiUrl + "?export_batch=" + batch.id);
        }
    };

    const headerTools = (
        <>
            <Link className="btn btn-sm btn-dark" to={url_reverse("market:promocode:log")}>
                <Awesome type="wheelchair" />
            </Link>{" "}
            <Link className="btn btn-sm btn-dark" to={url_reverse("market:promocode:ban")}>
                {t("Бан")}
            </Link>{" "}
            <Link className="btn btn-sm btn-dark" to={url_reverse("market:promocode:generation")}>
                <Awesome type="plus-circle" /> {t("Создать партию")}
            </Link>
        </>
    );

    return (
        <ListingCard header={header} headerTools={headerTools}>
            <div className="mb-3">
                <ButtonGroup>
                    <DevForm.Button
                        variant="secondary"
                        size="sm"
                        name="promocode_budget"
                        tooltip={t("Общий объём средств, выделенный на генерацию промокодов.")}
                    >
                        {t("Общий бюджет")}: {apiData.promocode_budget}
                    </DevForm.Button>
                    <DevForm.Button
                        variant="success"
                        size="sm"
                        name="remains_budget"
                        tooltip={t("Разница между общим бюджетом и сгенерированной суммой промокодов.")}
                    >
                        {t("Остаток бюджета")}: {apiData.promocode_budget - apiData.promocode_sum}
                    </DevForm.Button>
                    <DevForm.Button
                        variant="primary"
                        size="sm"
                        name="overal_sum"
                        tooltip={t(
                            "Совокупная сумма номиналов всех сгенерированных промокодов " +
                                "(«Не активирован» + «Активирован» + «Просрочен» + «Заблокирован»). " +
                                "Нажмите, чтобы отфильтровать таблицу."
                        )}
                    >
                        {t("Сгенерированная сумма промокодов")}: {apiData.promocode_sum || 0}
                    </DevForm.Button>
                    <DevForm.Button
                        variant="warning"
                        size="sm"
                        name="expired_sum"
                        tooltip={t(
                            "Общая сумма номиналов промокодов, срок действия которых истёк. " +
                                "Нажмите, чтобы отфильтровать таблицу."
                        )}
                    >
                        {t("Сумма просроченных промокодов")}: {apiData.expired_sum || 0}
                    </DevForm.Button>
                    <DevForm.Button
                        variant="danger"
                        size="sm"
                        name="banned_sum"
                        tooltip={t(
                            "Общая сумма номиналов промокодов, которые были заблокированы администратором. " +
                                "Нажмите, чтобы отфильтровать таблицу."
                        )}
                    >
                        {t("Сумма забаненных промокодов")}: {apiData.banned_sum || 0}
                    </DevForm.Button>
                </ButtonGroup>
            </div>
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("ID партии")}</th>
                        <th>
                            <OrderingHeader title={t("Название партии")} attr="name" />
                        </th>
                        <th>
                            <OrderingHeader title={t("Префикс")} attr="prefix" />
                        </th>
                        <th>
                            <OrderingHeader title={t("Количество промокодов в партии")} attr="quantity" />
                        </th>
                        <th>
                            <OrderingHeader title={t("Номинал")} attr="nominal" />
                        </th>
                        <th>
                            <OrderingHeader title={t("Общая сумма номиналов")} attr="nominal_sum" />
                        </th>
                        <th>
                            <OrderingHeader title={t("Срок годности")} attr="date_expiration" />
                        </th>
                        <th>
                            <OrderingHeader title={t("Кол-во активных кодов")} attr="active_cnt" />
                        </th>
                        <th>
                            <OrderingHeader title={t("Кол-во активированных кодов")} attr="activated_promo_cnt" />
                        </th>
                        <th>
                            <OrderingHeader title={t("Кол-во просроченных кодов")} attr="expired_promo_cnt" />
                        </th>
                        <th>
                            <OrderingHeader title={t("Кол-во заблокированных кодов")} attr="banned_promo_cnt" />
                        </th>
                        <th>{t("Ответственный")}</th>
                        <th>{t("Дата генерации")}</th>
                        <th style={{ width: "100px", align: "center" }} />
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((o, i) => {
                        return (
                            <tr
                                key={`object_list${i}`}
                                className={o.quantity === o.banned_promo_cnt ? "table-danger" : ""}
                            >
                                <td>{o.id}</td>
                                <td>{o.name}</td>
                                <td>{o.prefix}</td>
                                <td>{o.quantity}</td>
                                <td>{o.nominal}</td>
                                <td>{o.nominal_sum}</td>
                                <td>{o.date_expiration}</td>
                                <td>{o.active_cnt || 0}</td>
                                <td>{o.activated_promo_cnt || 0}</td>
                                <td>{o.expired_promo_cnt || 0}</td>
                                <td>{o.banned_promo_cnt || 0}</td>
                                <td>{o.author_email}</td>
                                <td>{o.date_added}</td>
                                <td className="dmg-table-row-tools">
                                    <DevForm.Button
                                        className="btn-success"
                                        size="sm"
                                        name="export"
                                        value={o.id}
                                        onClick={(ev) => getCSV(ev, o)}
                                    >
                                        <Awesome type="file-excel-o" tooltip={t("Выгрузить")} />
                                    </DevForm.Button>{" "}
                                    <DevForm.Button
                                        className="btn-danger"
                                        size="sm"
                                        name="delete"
                                        onClick={() =>
                                            setDelData({
                                                url: url_reverse("api:market:promocode:list", null, { ids: o.id }),
                                            })
                                        }
                                    >
                                        <Awesome type="trash-o" tooltip={t("Удалить")} />
                                    </DevForm.Button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </ListingCard>
    );
};

const BatchGeneration = ({ header }) => {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:market:promocode:generation"));

    if (!apiData) return;

    return (
        <CommonCard header={header}>
            <RenderForm backUrl={url_reverse("market:promocode:list")} submitButtonText={t("Сгенерировать")} />
        </CommonCard>
    );
};

const Log = ({ header }) => {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:market:promocode:log"));

    if (!apiData) return;

    return (
        <ListingCard header={header}>
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("Дата и время события")}</th>
                        <th>{t("Пользователь")}</th>
                        <th>{t("Действие")}</th>
                        <th>{t("Объект действия")}</th>
                        <th>
                            {t("Комментарий")} / {t("Описание")}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((o, i) => {
                        return (
                            <tr key={`object_list${i}`} className={o.is_error ? "table-danger" : ""}>
                                <td>{o.date_added}</td>
                                <td>{o.author_email}</td>
                                <td>{o.__action_get_display}</td>
                                <td>{o.entity_class}</td>
                                <td>{o.comment}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </ListingCard>
    );
};

const Ban = ({ header }) => {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:market:promocode:ban"));
    const [showConfirmation, setShowConfirmation] = useState(false);
    const onFormSubmit = useSubmit();

    if (!apiData) return;

    const confirmationClose = () => {
        setShowConfirmation(false);
    };

    return (
        <>
            <CommonCard header={header}>
                <Form>
                    <RenderForm bareForm={true} />
                    <DevForm.Button variant="primary" name="save" onClick={() => setShowConfirmation(true)}>
                        {t("Сохранить")}
                    </DevForm.Button>
                </Form>
                <DevModal
                    show={showConfirmation}
                    onHide={confirmationClose}
                    header={t("Подтверждение")}
                    body={t(
                        "Вы действительно хотите заблокировать выбранные промокоды? " +
                            "После блокировки их нельзя будет активировать или восстановить."
                    )}
                    footer={
                        <DevForm.Button
                            variant="primary"
                            type="submit"
                            name="ban"
                            onClick={(ev) =>
                                onFormSubmit(ev, {
                                    backUrl: url_reverse("market:promocode:list"),
                                    onFinally: () => confirmationClose(),
                                })
                            }
                        >
                            {t("Подтверждаю")}
                        </DevForm.Button>
                    }
                    renderWrapper={(children) => <Form>{children}</Form>}
                />
            </CommonCard>
        </>
    );
};
